import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import type {
  PatientListParams,
  PatientSortOption,
  PatientListItem,
} from '@/models/patient';
import type { DatePreset } from '@/models/report';
import type { TableProps } from 'antd';

function resolveStudyDateRange(
  preset: DatePreset | null,
  custom: [string, string] | null
): { study_date_from?: string; study_date_to?: string } {
  if (!preset) return {};
  const today = moment().format('YYYY-MM-DD');
  switch (preset) {
    case 'today':
      return { study_date_from: today, study_date_to: today };
    case 'last7':
      return {
        study_date_from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        study_date_to: today,
      };
    case 'last30':
      return {
        study_date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        study_date_to: today,
      };
    case 'custom':
      if (!custom) return {};
      return { study_date_from: custom[0], study_date_to: custom[1] };
    default:
      return {};
  }
}

export function usePatientListFilters() {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [sortOption, setSortOption] =
    useState<PatientSortOption>('latest_study_date');

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedStudyTypes, setSelectedStudyTypes] = useState<string[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset | null>(null);
  const [customDateRange, setCustomDateRange] = useState<
    [string, string] | null
  >(null);
  const [selectedReferringPhysicians, setSelectedReferringPhysicians] =
    useState<string[]>([]);
  const [selectedSonographers, setSelectedSonographers] = useState<string[]>(
    []
  );

  // ---- debounced search
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 350);
    return () => clearTimeout(id);
  }, [searchText]);

  // ---- derived flags
  const hasActiveFilters = useMemo(
    () =>
      selectedSites.length > 0 ||
      selectedStudyTypes.length > 0 ||
      datePreset !== null ||
      selectedReferringPhysicians.length > 0 ||
      selectedSonographers.length > 0,
    [
      selectedSites,
      selectedStudyTypes,
      datePreset,
      selectedReferringPhysicians,
      selectedSonographers,
    ]
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedSites.length > 0) count += 1;
    if (selectedStudyTypes.length > 0) count += 1;
    if (datePreset !== null) count += 1;
    if (selectedReferringPhysicians.length > 0) count += 1;
    if (selectedSonographers.length > 0) count += 1;
    return count;
  }, [
    selectedSites,
    selectedStudyTypes,
    datePreset,
    selectedReferringPhysicians,
    selectedSonographers,
  ]);

  const hasActiveSearch = debouncedSearch.length > 0;

  // ---- assembled params
  const params = useMemo<PatientListParams>(() => {
    const dateRange = resolveStudyDateRange(datePreset, customDateRange);
    return {
      page,
      size,
      ordering: sortOption,
      q: debouncedSearch || undefined,
      site: selectedSites.length > 0 ? selectedSites : undefined,
      study_type:
        selectedStudyTypes.length > 0 ? selectedStudyTypes : undefined,
      ...dateRange,
      referring_physician:
        selectedReferringPhysicians.length > 0
          ? selectedReferringPhysicians
          : undefined,
      sonographer:
        selectedSonographers.length > 0 ? selectedSonographers : undefined,
    };
  }, [
    page,
    size,
    sortOption,
    debouncedSearch,
    selectedSites,
    selectedStudyTypes,
    datePreset,
    customDateRange,
    selectedReferringPhysicians,
    selectedSonographers,
  ]);

  // ---- callbacks
  const onTableChange: TableProps<PatientListItem>['onChange'] = (
    pagination
  ) => {
    setPage(pagination?.current ?? page);
    setSize(pagination?.pageSize ?? size);
  };

  const clearFilters = () => {
    setSelectedSites([]);
    setSelectedStudyTypes([]);
    setDatePreset(null);
    setCustomDateRange(null);
    setSelectedReferringPhysicians([]);
    setSelectedSonographers([]);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchText('');
    setDebouncedSearch('');
    setPage(1);
  };

  return {
    params,
    searchText,
    setSearchText,
    sortOption,
    setSortOption: (opt: PatientSortOption) => {
      setSortOption(opt);
      setPage(1);
    },
    selectedSites,
    onSitesChange: setSelectedSites,
    selectedStudyTypes,
    onStudyTypesChange: setSelectedStudyTypes,
    datePreset,
    onDatePresetChange: (preset: DatePreset | null) => {
      setDatePreset(preset);
      if (preset !== 'custom') setCustomDateRange(null);
      setPage(1);
    },
    customDateRange,
    onCustomDateRangeChange: setCustomDateRange,
    selectedReferringPhysicians,
    onReferringPhysiciansChange: setSelectedReferringPhysicians,
    selectedSonographers,
    onSonographersChange: setSelectedSonographers,
    hasActiveFilters,
    activeFilterCount,
    hasActiveSearch,
    clearFilters,
    clearSearch,
    onTableChange,
  };
}
